import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { Toaster } from '../components/ui/sonner';
import { StatusBadge, RequestDetailModal } from '../components/RequestDetail';
import Avatar from '../components/Avatar';
import { Link } from 'react-router-dom';
import { FileText, X, Send, Download, Sparkles, Trophy, Users } from 'lucide-react';
import TopMatches from '../components/TopMatches';
import RecommendedSalespeople from '../components/RecommendedSalespeople';
import BoostModal from '../components/BoostModal';
import DashboardStatsBar from '../components/DashboardStatsBar';
import ShortlistedSalespeople from '../components/ShortlistedSalespeople';
import PostAnalyticsCard from '../components/PostAnalyticsCard';
import PostPerformance from '../components/PostPerformance';
import ConversionFunnel from '../components/ConversionFunnel';
import NotificationBell from '../components/NotificationBell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const BusinessDashboard = () => {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [isRequestsOpen, setIsRequestsOpen] = useState(false);
  const [selectedPost, setSelectedPost] = useState(null);
  const [salesRequests, setSalesRequests] = useState([]);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestDetailOpen, setRequestDetailOpen] = useState(false);
  const [exportStatusFilter, setExportStatusFilter] = useState('');
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [boostModalOpen, setBoostModalOpen] = useState(false);
  const [matchesPostId, setMatchesPostId] = useState(null);
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: '',
    price: '',
    rules_of_engagement: '',
    include_locations: [],
    exclude_locations: [],
    applicationForm: [],
    defaultTerms: {
      commissionPct: '',
      notes: ''
    },
    // New matching fields
    industry: '',
    acv_band: '',
    sales_motion: '',
    territory: '',
    languages: []
  });
  const [languageInput, setLanguageInput] = useState('');
  const [locationForm, setLocationForm] = useState({
    type: 'include',
    country: '',
    region: '',
    city: ''
  });

  useEffect(() => {
    fetchPosts();
    fetchCountries();
  }, []);

  useEffect(() => {
    if (locationForm.country) {
      fetchRegions(locationForm.country);
    }
  }, [locationForm.country]);

  useEffect(() => {
    if (locationForm.country && locationForm.region) {
      fetchCities(locationForm.country, locationForm.region);
    }
  }, [locationForm.country, locationForm.region]);

  const fetchCountries = async () => {
    try {
      const response = await axios.get(`${API}/locations/countries`);
      setCountries(response.data);
    } catch (error) {
      console.error('Failed to fetch countries:', error);
    }
  };

  const fetchRegions = async (countryCode) => {
    try {
      const response = await axios.get(`${API}/locations/regions/${countryCode}`);
      setRegions(response.data);
    } catch (error) {
      console.error('Failed to fetch regions:', error);
    }
  };

  const fetchCities = async (countryCode, region) => {
    try {
      const response = await axios.get(`${API}/locations/cities/${countryCode}/${region}`);
      setCities(response.data);
    } catch (error) {
      console.error('Failed to fetch cities:', error);
    }
  };

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/business/posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPosts(response.data);
    } catch (error) {
      toast.error('Failed to fetch posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchPostRequests = async (postId) => {
    try {
      const response = await axios.get(`${API}/requests/for-post/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSalesRequests(response.data);
    } catch (error) {
      toast.error('Failed to fetch requests');
    }
  };

  const openRequestDetail = (request) => {
    setSelectedRequest(request);
    setRequestDetailOpen(true);
  };

  const handleExportCSV = async () => {
    try {
      const url = exportStatusFilter 
        ? `${API}/requests/export.csv?status_filter=${exportStatusFilter}`
        : `${API}/requests/export.csv`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `requests_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);
      toast.success('CSV downloaded successfully!');
    } catch (error) {
      toast.error('Failed to export CSV');
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    try {
      // Prepare payload with proper null handling for optional fields
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        rules_of_engagement: formData.rules_of_engagement,
        include_locations: formData.include_locations || [],
        exclude_locations: formData.exclude_locations || [],
        applicationForm: formData.applicationForm || [],
        // Convert empty strings to null for optional matching fields
        industry: formData.industry || null,
        acv_band: formData.acv_band || null,
        sales_motion: formData.sales_motion || null,
        territory: formData.territory || null,
        languages: formData.languages || []
      };
      
      // Only include defaultTerms if commission is set
      if (formData.defaultTerms?.commissionPct) {
        const pct = parseFloat(formData.defaultTerms.commissionPct);
        if (pct >= 0 && pct <= 60) {
          payload.defaultTerms = {
            commissionPct: pct,
            notes: formData.defaultTerms.notes || null
          };
        }
      }
      
      console.log('Creating post with payload:', payload);
      
      const response = await axios.post(
        `${API}/business/posts`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      console.log('Post created successfully:', response.data);
      toast.success('Post created successfully!');
      setIsCreateOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Create post error:', error);
      console.error('Error response:', error.response?.data);
      toast.error(error.response?.data?.detail || error.message || 'Failed to create post');
    }
  };

  const handleUpdatePost = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: formData.title,
        description: formData.description,
        category: formData.category,
        price: parseFloat(formData.price),
        rules_of_engagement: formData.rules_of_engagement,
        include_locations: formData.include_locations,
        exclude_locations: formData.exclude_locations,
        // Convert empty strings to null
        industry: formData.industry || null,
        acv_band: formData.acv_band || null,
        sales_motion: formData.sales_motion || null,
        territory: formData.territory || null,
        languages: formData.languages || []
      };
      
      // Include defaultTerms if set
      if (formData.defaultTerms?.commissionPct) {
        const pct = parseFloat(formData.defaultTerms.commissionPct);
        if (pct >= 0 && pct <= 60) {
          payload.defaultTerms = {
            commissionPct: pct,
            notes: formData.defaultTerms.notes || null
          };
        }
      }
      
      await axios.put(
        `${API}/business/posts/${selectedPost.id}`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Post updated successfully!');
      setIsEditOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      console.error('Update post error:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update post');
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API}/business/posts/${postId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Post deleted successfully!');
      fetchPosts();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  const openEditDialog = (post) => {
    setSelectedPost(post);
    setFormData({
      title: post.title,
      description: post.description,
      category: post.category,
      price: post.price.toString(),
      rules_of_engagement: post.rules_of_engagement,
      include_locations: post.include_locations,
      exclude_locations: post.exclude_locations,
      defaultTerms: post.defaultTerms || { commissionPct: '', notes: '' },
      industry: post.industry || '',
      acv_band: post.acv_band || '',
      sales_motion: post.sales_motion || '',
      territory: post.territory || '',
      languages: post.languages || []
    });
    setIsEditOpen(true);
  };

  const openRequestsDialog = (post) => {
    setSelectedPost(post);
    fetchPostRequests(post.id);
    setIsRequestsOpen(true);
  };

  const addLocation = () => {
    if (!locationForm.country) {
      toast.error('Please select a country');
      return;
    }

    const newLocation = {
      country: locationForm.country,
      region: locationForm.region || null,
      city: locationForm.city || null
    };

    const locationKey = locationForm.type === 'include' ? 'include_locations' : 'exclude_locations';
    setFormData(prev => ({
      ...prev,
      [locationKey]: [...prev[locationKey], newLocation]
    }));

    setLocationForm({ type: 'include', country: '', region: '', city: '' });
    setRegions([]);
    setCities([]);
  };

  const removeLocation = (type, index) => {
    const locationKey = type === 'include' ? 'include_locations' : 'exclude_locations';
    setFormData(prev => ({
      ...prev,
      [locationKey]: prev[locationKey].filter((_, i) => i !== index)
    }));
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      category: '',
      price: '',
      rules_of_engagement: '',
      include_locations: [],
      exclude_locations: [],
      applicationForm: [],
      defaultTerms: {
        commissionPct: '',
        notes: ''
      },
      industry: '',
      acv_band: '',
      sales_motion: '',
      territory: '',
      languages: []
    });
    setLanguageInput('');
    setLocationForm({ type: 'include', country: '', region: '', city: '' });
    setSelectedPost(null);
  };

  const formatLocation = (loc) => {
    return [loc.city, loc.region, loc.country].filter(Boolean).join(', ');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-bg">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-gold"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-brand-bg">
      <Toaster position="top-center" richColors />
      
      {/* Header */}
      <header className="bg-brand-surface/80 backdrop-blur border-b border-zinc-800 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-3">
                <img src="/logo.svg" alt="Sale Sphere" className="h-7 w-7" />
                <div>
                  <h1 className="font-display text-xl text-brand-text tracking-wide">Sale Sphere</h1>
                  <p className="text-sm text-brand-muted">Business Dashboard</p>
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              {user.role === 'ADMIN' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => navigate('/admin')}
                  className="border-purple-500 text-purple-500 hover:bg-purple-500/10 rounded-xl"
                >
                  Admin
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/salespeople')}
                className="border-brand-gold text-brand-gold hover:bg-brand-gold/10 rounded-xl"
              >
                Find Salespeople
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/leaderboard')}
                className="border-brand-gold text-brand-gold hover:bg-brand-gold/10 rounded-xl"
                data-testid="nav-leaderboard"
              >
                <Trophy className="w-4 h-4 mr-1" />
                Leaderboard
              </Button>
              <NotificationBell />
              <Link to="/settings/profile" className="hover:opacity-80 transition-opacity">
                <Avatar user={user} size="sm" />
              </Link>
              <Dialog>
                <DialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="border-brand-gold text-brand-gold hover:bg-brand-gold/10 rounded-xl"
                  >
                    Export CSV
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Export Requests to CSV</DialogTitle>
                    <DialogDescription>
                      Export all requests for your posts to a CSV file
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 mt-4">
                    <div>
                      <Label>Filter by Status (Optional)</Label>
                      <Select value={exportStatusFilter} onValueChange={setExportStatusFilter}>
                        <SelectTrigger>
                          <SelectValue placeholder="All statuses" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="">All statuses</SelectItem>
                          <SelectItem value="REQUESTED">REQUESTED</SelectItem>
                          <SelectItem value="APPROVED">APPROVED</SelectItem>
                          <SelectItem value="REJECTED">REJECTED</SelectItem>
                          <SelectItem value="REVOKED">REVOKED</SelectItem>
                          <SelectItem value="CLOSED">CLOSED</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button 
                      onClick={handleExportCSV} 
                      className="w-full bg-brand-gold hover:bg-brand-gold/90 text-black"
                    >
                      Download CSV
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
              <Button
                variant="outline"
                size="sm"
                onClick={logout}
                className="border-brand-gold text-brand-gold hover:bg-brand-gold/10 rounded-xl"
                data-testid="logout-button"
              >
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Dashboard Stats */}
        <DashboardStatsBar token={token} />
        
        {/* Conversion Funnel */}
        <ConversionFunnel token={token} />

        {/* Shortlisted Salespeople */}
        <ShortlistedSalespeople token={token} />
        
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold text-brand-text">Your Posts</h2>
          <Button 
            className="bg-brand-gold hover:bg-brand-gold600 text-black font-medium rounded-xl shadow-soft" 
            data-testid="create-post-button"
            onClick={() => setIsCreateOpen(true)}
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Create Post
          </Button>
          
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="bg-brand-gold hover:bg-brand-gold600 text-black font-medium rounded-xl shadow-soft hidden">
                Hidden Trigger
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-brand-surface border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-brand-text">Create New Post</DialogTitle>
                <DialogDescription className="text-brand-muted">Fill in the details for your new product or service post.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreatePost} className="space-y-4 mt-4">
                {/* Basic Fields */}
                <div>
                  <Label htmlFor="title">Title</Label>
                  <Input 
                    id="title"
                    value={formData?.title || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                    required
                    data-testid="post-title-input"
                  />
                </div>
                
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea 
                    id="description"
                    value={formData?.description || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                    rows={3}
                    required
                    data-testid="post-description-input"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="category">Category</Label>
                    <Input 
                      id="category"
                      value={formData?.category || ''} 
                      onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
                      required
                      data-testid="post-category-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="price">Price ($)</Label>
                    <Input 
                      id="price"
                      type="number"
                      step="0.01"
                      value={formData?.price || ''} 
                      onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                      required
                      data-testid="post-price-input"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="rules">Rules of Engagement</Label>
                  <Textarea 
                    id="rules"
                    value={formData?.rules_of_engagement || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, rules_of_engagement: e.target.value}))}
                    rows={2}
                    required
                    data-testid="post-rules-input"
                  />
                </div>

                {/* Location Filtering */}
                <div className="space-y-4 border-t pt-4 border-zinc-800">
                  <h3 className="font-medium text-brand-text">Location Targeting (Optional)</h3>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="loc-type">Type</Label>
                      <select 
                        id="loc-type"
                        value={locationForm.type}
                        onChange={(e) => setLocationForm(prev => ({...prev, type: e.target.value}))}
                        className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      >
                        <option value="include">Include</option>
                        <option value="exclude">Exclude</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="loc-country">Country</Label>
                      <select 
                        id="loc-country"
                        value={locationForm.country}
                        onChange={(e) => {
                          setLocationForm(prev => ({...prev, country: e.target.value, region: '', city: ''}));
                          if (e.target.value) fetchRegions(e.target.value);
                        }}
                        className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      >
                        <option value="">Select country</option>
                        {countries.map((country) => (
                          <option key={country.code} value={country.code}>{country.name}</option>
                        ))}
                      </select>
                    </div>
                    {locationForm.country && (
                      <div>
                        <Label htmlFor="loc-region">Region (Optional)</Label>
                        <select 
                          id="loc-region"
                          value={locationForm.region}
                          onChange={(e) => {
                            setLocationForm(prev => ({...prev, region: e.target.value, city: ''}));
                            if (e.target.value) fetchCities(locationForm.country, e.target.value);
                          }}
                          className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                        >
                          <option value="">All regions</option>
                          {regions.map((region) => (
                            <option key={region} value={region}>{region}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                  {locationForm.country && locationForm.region && cities.length > 0 && (
                    <div>
                      <Label htmlFor="loc-city">City (Optional)</Label>
                      <select 
                        id="loc-city"
                        value={locationForm.city}
                        onChange={(e) => setLocationForm(prev => ({...prev, city: e.target.value}))}
                        className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      >
                        <option value="">All cities</option>
                        {cities.map((city) => (
                          <option key={city} value={city}>{city}</option>
                        ))}
                      </select>
                    </div>
                  )}
                  <Button type="button" variant="outline" onClick={addLocation}>
                    Add Location
                  </Button>
                  
                  {/* Display added locations */}
                  {formData.include_locations.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-green-500">Included Locations:</Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.include_locations.map((loc, idx) => (
                          <Badge key={idx} variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                            {formatLocation(loc)}
                            <X className="ml-2 h-3 w-3 cursor-pointer" onClick={() => removeLocation('include', idx)} />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                  {formData.exclude_locations.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-red-500">Excluded Locations:</Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.exclude_locations.map((loc, idx) => (
                          <Badge key={idx} variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                            {formatLocation(loc)}
                            <X className="ml-2 h-3 w-3 cursor-pointer" onClick={() => removeLocation('exclude', idx)} />
                          </Badge>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Matching & Discovery Fields */}
                <div className="space-y-4 border-t pt-4 border-zinc-800">
                  <h3 className="font-medium text-brand-text">Matching Criteria (Optional)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="industry">Industry</Label>
                      <select 
                        id="industry"
                        value={formData?.industry || ''} 
                        onChange={(e) => setFormData(prev => ({...prev, industry: e.target.value}))}
                        className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      >
                        <option value="">Any Industry</option>
                        <option value="Software">Software</option>
                        <option value="Hardware">Hardware</option>
                        <option value="Healthcare">Healthcare</option>
                        <option value="Finance">Finance</option>
                        <option value="Real Estate">Real Estate</option>
                        <option value="Manufacturing">Manufacturing</option>
                        <option value="Retail">Retail</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="acv">Deal Size (ACV)</Label>
                      <select 
                        id="acv"
                        value={formData?.acv_band || ''} 
                        onChange={(e) => setFormData(prev => ({...prev, acv_band: e.target.value}))}
                        className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      >
                        <option value="">Any Size</option>
                        <option value="0-10k">$0 - $10k</option>
                        <option value="10k-50k">$10k - $50k</option>
                        <option value="50k-100k">$50k - $100k</option>
                        <option value="100k+">$100k+</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="motion">Sales Motion</Label>
                      <select 
                        id="motion"
                        value={formData?.sales_motion || ''} 
                        onChange={(e) => setFormData(prev => ({...prev, sales_motion: e.target.value}))}
                        className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                      >
                        <option value="">Any Motion</option>
                        <option value="SDR">SDR (Outbound)</option>
                        <option value="AE">AE (Full-cycle)</option>
                        <option value="Channel">Channel</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="territory">Territory</Label>
                      <Input 
                        id="territory"
                        value={formData?.territory || ''} 
                        onChange={(e) => setFormData(prev => ({...prev, territory: e.target.value}))}
                        placeholder="e.g., Ontario, California"
                      />
                    </div>
                  </div>
                </div>

                {/* Default Terms */}
                <div className="space-y-4 border-t pt-4 border-zinc-800">
                  <h3 className="font-medium text-brand-text">Default Terms (Optional)</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="commission">Commission (%)</Label>
                      <Input 
                        id="commission"
                        type="number"
                        min="0"
                        max="60"
                        step="0.5"
                        value={formData?.defaultTerms?.commissionPct || ''} 
                        onChange={(e) => setFormData(prev => ({...prev, defaultTerms: {...prev.defaultTerms, commissionPct: e.target.value}}))}
                        placeholder="0-60"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => { setIsCreateOpen(false); resetForm(); }}>Cancel</Button>
                  <Button type="submit" className="bg-brand-gold hover:bg-brand-gold600" data-testid="submit-post-button">Create Post</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {posts.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-brand-muted text-brand-muted">No posts yet. Create your first post to get started!</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {posts.map((post) => (
              <Card key={post.id} className="hover:shadow-lg transition-shadow" data-testid="post-card">
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{post.title}</CardTitle>
                      <CardDescription className="mt-1">{post.category}</CardDescription>
                    </div>
                    <Badge variant="secondary" className="ml-2">
                      ${post.price}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-brand-muted text-brand-muted mb-4 line-clamp-3">
                    {post.description}
                  </p>
                  <div className="space-y-2 text-xs text-brand-muted text-brand-muted">
                    {post.include_locations.length > 0 && (
                      <div>
                        <span className="font-medium">Include: </span>
                        {post.include_locations.map(formatLocation).join('; ')}
                      </div>
                    )}
                    {post.exclude_locations.length > 0 && (
                      <div>
                        <span className="font-medium">Exclude: </span>
                        {post.exclude_locations.map(formatLocation).join('; ')}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 mt-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openRequestsDialog(post)}
                      className="flex-1"
                      data-testid="view-requests-button"
                    >
                      Requests
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openEditDialog(post)}
                      data-testid="edit-post-button"
                    >
                      Edit
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeletePost(post.id)}
                      data-testid="delete-post-button"
                    >
                      Delete
                    </Button>
                  </div>
                  {/* Post Analytics */}
                  <div className="mt-3">
                    <PostAnalyticsCard postId={post.id} token={token} />
                  </div>
                  {/* Salesperson Performance */}
                  <PostPerformance postId={post.id} token={token} />
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-brand-surface border-zinc-800">
          <DialogHeader>
            <DialogTitle className="text-brand-text">Edit Post</DialogTitle>
            <DialogDescription className="text-brand-muted">Update the details of your post.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdatePost} className="space-y-4 mt-4">
            {/* Basic Fields */}
            <div>
              <Label htmlFor="edit-title">Title</Label>
              <Input 
                id="edit-title"
                value={formData?.title || ''} 
                onChange={(e) => setFormData(prev => ({...prev, title: e.target.value}))}
                required
              />
            </div>
            
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea 
                id="edit-description"
                value={formData?.description || ''} 
                onChange={(e) => setFormData(prev => ({...prev, description: e.target.value}))}
                rows={3}
                required
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="edit-category">Category</Label>
                <Input 
                  id="edit-category"
                  value={formData?.category || ''} 
                  onChange={(e) => setFormData(prev => ({...prev, category: e.target.value}))}
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-price">Price ($)</Label>
                <Input 
                  id="edit-price"
                  type="number"
                  step="0.01"
                  value={formData?.price || ''} 
                  onChange={(e) => setFormData(prev => ({...prev, price: e.target.value}))}
                  required
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="edit-rules">Rules of Engagement</Label>
              <Textarea 
                id="edit-rules"
                value={formData?.rules_of_engagement || ''} 
                onChange={(e) => setFormData(prev => ({...prev, rules_of_engagement: e.target.value}))}
                rows={2}
                required
              />
            </div>

            {/* Location Filtering */}
            <div className="space-y-4 border-t pt-4 border-zinc-800">
              <h3 className="font-medium text-brand-text">Location Targeting (Optional)</h3>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="edit-loc-type">Type</Label>
                  <select 
                    id="edit-loc-type"
                    value={locationForm.type}
                    onChange={(e) => setLocationForm(prev => ({...prev, type: e.target.value}))}
                    className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="include">Include</option>
                    <option value="exclude">Exclude</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit-loc-country">Country</Label>
                  <select 
                    id="edit-loc-country"
                    value={locationForm.country}
                    onChange={(e) => {
                      setLocationForm(prev => ({...prev, country: e.target.value, region: '', city: ''}));
                      if (e.target.value) fetchRegions(e.target.value);
                    }}
                    className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="">Select country</option>
                    {countries.map((country) => (
                      <option key={country.code} value={country.code}>{country.name}</option>
                    ))}
                  </select>
                </div>
                {locationForm.country && (
                  <div>
                    <Label htmlFor="edit-loc-region">Region (Optional)</Label>
                    <select 
                      id="edit-loc-region"
                      value={locationForm.region}
                      onChange={(e) => {
                        setLocationForm(prev => ({...prev, region: e.target.value, city: ''}));
                        if (e.target.value) fetchCities(locationForm.country, e.target.value);
                      }}
                      className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                    >
                      <option value="">All regions</option>
                      {regions.map((region) => (
                        <option key={region} value={region}>{region}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              {locationForm.country && locationForm.region && cities.length > 0 && (
                <div>
                  <Label htmlFor="edit-loc-city">City (Optional)</Label>
                  <select 
                    id="edit-loc-city"
                    value={locationForm.city}
                    onChange={(e) => setLocationForm(prev => ({...prev, city: e.target.value}))}
                    className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="">All cities</option>
                    {cities.map((city) => (
                      <option key={city} value={city}>{city}</option>
                    ))}
                  </select>
                </div>
              )}
              <Button type="button" variant="outline" onClick={addLocation}>
                Add Location
              </Button>
              
              {/* Display added locations */}
              {formData.include_locations && formData.include_locations.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-green-500">Included Locations:</Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.include_locations.map((loc, idx) => (
                      <Badge key={idx} variant="outline" className="bg-green-500/10 text-green-500 border-green-500/20">
                        {formatLocation(loc)}
                        <X className="ml-2 h-3 w-3 cursor-pointer" onClick={() => removeLocation('include', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              {formData.exclude_locations && formData.exclude_locations.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-red-500">Excluded Locations:</Label>
                  <div className="flex flex-wrap gap-2">
                    {formData.exclude_locations.map((loc, idx) => (
                      <Badge key={idx} variant="outline" className="bg-red-500/10 text-red-500 border-red-500/20">
                        {formatLocation(loc)}
                        <X className="ml-2 h-3 w-3 cursor-pointer" onClick={() => removeLocation('exclude', idx)} />
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Matching & Discovery Fields */}
            <div className="space-y-4 border-t pt-4 border-zinc-800">
              <h3 className="font-medium text-brand-text">Matching Criteria (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-industry">Industry</Label>
                  <select 
                    id="edit-industry"
                    value={formData?.industry || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, industry: e.target.value}))}
                    className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="">Any Industry</option>
                    <option value="Software">Software</option>
                    <option value="Hardware">Hardware</option>
                    <option value="Healthcare">Healthcare</option>
                    <option value="Finance">Finance</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Manufacturing">Manufacturing</option>
                    <option value="Retail">Retail</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit-acv">Deal Size (ACV)</Label>
                  <select 
                    id="edit-acv"
                    value={formData?.acv_band || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, acv_band: e.target.value}))}
                    className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="">Any Size</option>
                    <option value="0-10k">$0 - $10k</option>
                    <option value="10k-50k">$10k - $50k</option>
                    <option value="50k-100k">$50k - $100k</option>
                    <option value="100k+">$100k+</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit-motion">Sales Motion</Label>
                  <select 
                    id="edit-motion"
                    value={formData?.sales_motion || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, sales_motion: e.target.value}))}
                    className="flex h-10 w-full rounded-md border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-white"
                  >
                    <option value="">Any Motion</option>
                    <option value="SDR">SDR (Outbound)</option>
                    <option value="AE">AE (Full-cycle)</option>
                    <option value="Channel">Channel</option>
                  </select>
                </div>
                <div>
                  <Label htmlFor="edit-territory">Territory</Label>
                  <Input 
                    id="edit-territory"
                    value={formData?.territory || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, territory: e.target.value}))}
                    placeholder="e.g., Ontario, California"
                  />
                </div>
              </div>
            </div>

            {/* Default Terms */}
            <div className="space-y-4 border-t pt-4 border-zinc-800">
              <h3 className="font-medium text-brand-text">Default Terms (Optional)</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="edit-commission">Commission (%)</Label>
                  <Input 
                    id="edit-commission"
                    type="number"
                    min="0"
                    max="60"
                    step="0.5"
                    value={formData?.defaultTerms?.commissionPct || ''} 
                    onChange={(e) => setFormData(prev => ({...prev, defaultTerms: {...(prev.defaultTerms || {}), commissionPct: e.target.value}}))}
                    placeholder="0-60"
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => { setIsEditOpen(false); resetForm(); }}>
                Cancel
              </Button>
              <Button type="submit" className="bg-brand-gold hover:bg-brand-gold600">
                Update Post
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Requests Dialog */}
      <Dialog open={isRequestsOpen} onOpenChange={setIsRequestsOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Sales Requests for "{selectedPost?.title}"</DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {salesRequests.length === 0 ? (
              <p className="text-center text-brand-muted text-brand-muted py-8">No requests yet</p>
            ) : (
              <div className="space-y-3">
                {salesRequests.map((request) => (
                  <Card key={request.id} data-testid="sales-request-card" className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => openRequestDetail(request)}>
                    <CardContent className="py-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium text-brand-text text-brand-text">{request.salesperson_name}</p>
                          <p className="text-sm text-brand-muted text-brand-muted">{request.salesperson_email}</p>
                          <p className="text-xs text-brand-muted dark:text-brand-muted mt-1">
                            {new Date(request.created_at).toLocaleDateString()}
                          </p>
                        </div>
                        <StatusBadge status={request.status} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
          {/* Recommended Salespeople */}
          {selectedPost && (
            <div className="mt-6">
              <RecommendedSalespeople postId={selectedPost.id} postTitle={selectedPost.title} token={token} />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <RequestDetailModal
        request={selectedRequest}
        open={requestDetailOpen}
        onClose={() => {
          setRequestDetailOpen(false);
          if (selectedPost) {
            fetchPostRequests(selectedPost.id);
          }
        }}
        token={token}
        userRole="BUSINESS"
        currentUser={user}
        onUpdate={() => {
          if (selectedPost) {
            fetchPostRequests(selectedPost.id);
          }
        }}
      />
    </div>
  );
};

// Reusable Post Form Component
const PostForm = ({
  formData,
  setFormData,
  locationForm,
  setLocationForm,
  countries,
  regions,
  cities,
  addLocation,
  removeLocation,
  formatLocation,
  languageInput,
  setLanguageInput
}) => {
  return (
    <>
      <div className="space-y-2">
        <Label htmlFor="title">Title</Label>
        <Input
          id="title"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          required
          data-testid="post-title-input"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          value={formData.description}
          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
          required
          data-testid="post-description-input"
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="category">Category</Label>
          <Input
            id="category"
            value={formData.category}
            onChange={(e) => setFormData(prev => ({ ...prev, category: e.target.value }))}
            required
            data-testid="post-category-input"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="price">Price ($)</Label>
          <Input
            id="price"
            type="number"
            step="0.01"
            value={formData.price}
            onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
            required
            data-testid="post-price-input"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="rules">Rules of Engagement</Label>
        <Textarea
          id="rules"
          value={formData.rules_of_engagement}
          onChange={(e) => setFormData(prev => ({ ...prev, rules_of_engagement: e.target.value }))}
          rows={2}
          required
          data-testid="post-rules-input"
        />
      </div>

      {/* Matching & Discovery Fields */}
      <div className="space-y-4 border-t pt-4">
        <h3 className="font-medium text-brand-text">Matching Criteria (Optional)</h3>
        <p className="text-sm text-brand-muted">
          Help us find the best salespeople for your needs
        </p>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="industry">Industry</Label>
            <Select 
              value={formData.industry}
              onValueChange={(val) => setFormData(prev => ({ ...prev, industry: val }))}
            >
              <SelectTrigger data-testid="post-industry-select">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any Industry</SelectItem>
                <SelectItem value="Software">Software</SelectItem>
                <SelectItem value="Hardware">Hardware</SelectItem>
                <SelectItem value="Healthcare">Healthcare</SelectItem>
                <SelectItem value="Finance">Finance</SelectItem>
                <SelectItem value="Real Estate">Real Estate</SelectItem>
                <SelectItem value="Manufacturing">Manufacturing</SelectItem>
                <SelectItem value="Retail">Retail</SelectItem>
                <SelectItem value="Other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="acv">Deal Size (ACV)</Label>
            <Select 
              value={formData.acv_band}
              onValueChange={(val) => setFormData(prev => ({ ...prev, acv_band: val }))}
            >
              <SelectTrigger data-testid="post-acv-select">
                <SelectValue placeholder="Select ACV band" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any Size</SelectItem>
                <SelectItem value="0-10k">$0 - $10k</SelectItem>
                <SelectItem value="10k-50k">$10k - $50k</SelectItem>
                <SelectItem value="50k-100k">$50k - $100k</SelectItem>
                <SelectItem value="100k+">$100k+</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="sales-motion">Sales Motion</Label>
            <Select 
              value={formData.sales_motion}
              onValueChange={(val) => setFormData(prev => ({ ...prev, sales_motion: val }))}
            >
              <SelectTrigger data-testid="post-motion-select">
                <SelectValue placeholder="Select sales motion" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Any Motion</SelectItem>
                <SelectItem value="SDR">SDR (Outbound)</SelectItem>
                <SelectItem value="AE">AE (Full-cycle)</SelectItem>
                <SelectItem value="Channel">Channel</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="territory">Territory</Label>
            <Input
              id="territory"
              value={formData.territory}
              onChange={(e) => setFormData(prev => ({ ...prev, territory: e.target.value }))}
              placeholder="e.g., Ontario, California"
              data-testid="post-territory-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="languages">Languages Required</Label>
          <div className="flex gap-2">
            <Input
              id="languages"
              value={languageInput}
              onChange={(e) => setLanguageInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (languageInput.trim() && !formData.languages.includes(languageInput.trim())) {
                    setFormData(prev => ({ 
                      ...prev, 
                      languages: [...prev.languages, languageInput.trim()] 
                    }));
                    setLanguageInput('');
                  }
                }
              }}
              placeholder="Type and press Enter"
              data-testid="post-language-input"
            />
          </div>
          {formData.languages.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {formData.languages.map((lang, idx) => (
                <Badge 
                  key={idx}
                  variant="outline" 
                  className="bg-brand-gold/10 text-brand-gold border-brand-gold/20 cursor-pointer hover:bg-red-500/10"
                  onClick={() => setFormData(prev => ({
                    ...prev,
                    languages: prev.languages.filter((_, i) => i !== idx)
                  }))}
                >
                  {lang} ✕
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Default Terms Section */}
      <div className="space-y-4 border-t pt-4">
        <h3 className="font-medium text-brand-text">Default Terms (Optional)</h3>
        <p className="text-sm text-brand-muted">
          Set default commission terms for salespeople requesting this post
        </p>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="commission">Commission (%)</Label>
            <Input
              id="commission"
              type="number"
              min="0"
              max="60"
              step="0.5"
              value={formData.defaultTerms.commissionPct}
              onChange={(e) => setFormData(prev => ({
                ...prev,
                defaultTerms: { ...prev.defaultTerms, commissionPct: e.target.value }
              }))}
              placeholder="0-60"
              data-testid="default-commission-input"
            />
            <p className="text-xs text-brand-muted">
              Enter a value between 0 and 60%
            </p>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="commission-notes">Commission Notes</Label>
          <Textarea
            id="commission-notes"
            value={formData.defaultTerms.notes}
            onChange={(e) => setFormData(prev => ({
              ...prev,
              defaultTerms: { ...prev.defaultTerms, notes: e.target.value }
            }))}
            rows={2}
            placeholder="e.g., Bonus for enterprise deals"
            data-testid="default-commission-notes"
          />
        </div>
      </div>

      <div className="space-y-4 border-t pt-4">
        <h3 className="font-medium text-brand-text text-brand-text">Location Filters</h3>
        
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Filter Type</Label>
            <Select
              value={locationForm.type}
              onValueChange={(value) => setLocationForm(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="include">Include</SelectItem>
                <SelectItem value="exclude">Exclude</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Country</Label>
            <Select
              value={locationForm.country}
              onValueChange={(value) => {
                setLocationForm(prev => ({ ...prev, country: value, region: '', city: '' }));
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select country" />
              </SelectTrigger>
              <SelectContent>
                {countries.map((country) => (
                  <SelectItem key={country.code} value={country.code}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {locationForm.country && (
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Region (Optional)</Label>
              <Select
                value={locationForm.region}
                onValueChange={(value) => {
                  setLocationForm(prev => ({ ...prev, region: value, city: '' }));
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select region" />
                </SelectTrigger>
                <SelectContent>
                  {regions.map((region) => (
                    <SelectItem key={region} value={region}>
                      {region}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {locationForm.region && (
              <div className="space-y-2">
                <Label>City (Optional)</Label>
                <Select
                  value={locationForm.city}
                  onValueChange={(value) => setLocationForm(prev => ({ ...prev, city: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select city" />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city} value={city}>
                        {city}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        )}

        <Button type="button" variant="outline" onClick={addLocation} size="sm" data-testid="add-location-button">
          Add Location Filter
        </Button>

        {(formData.include_locations.length > 0 || formData.exclude_locations.length > 0) && (
          <div className="space-y-2">
            {formData.include_locations.length > 0 && (
              <div>
                <p className="text-sm font-medium text-brand-text text-brand-text mb-2">Include Locations:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.include_locations.map((loc, idx) => (
                    <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                      {formatLocation(loc)}
                      <button
                        type="button"
                        onClick={() => removeLocation('include', idx)}
                        className="ml-1 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
            {formData.exclude_locations.length > 0 && (
              <div>
                <p className="text-sm font-medium text-brand-text text-brand-text mb-2">Exclude Locations:</p>
                <div className="flex flex-wrap gap-2">
                  {formData.exclude_locations.map((loc, idx) => (
                    <Badge key={idx} variant="destructive" className="flex items-center gap-1">
                      {formatLocation(loc)}
                      <button
                        type="button"
                        onClick={() => removeLocation('exclude', idx)}
                        className="ml-1 hover:text-red-600"
                      >
                        ×
                      </button>
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
};

export default BusinessDashboard;
